// Round 1, lane A (docs/briefs/ROUND1-SPEC-2026-09-05.md section B): literal text. One escape
// character, the backslash. Parsing: a backslash followed by any character yields that character and
// carries no grammar; a backslash at the very end of a line is a literal backslash; a line whose first
// non-space character is a backslash is text, never a construct. Decompiling: a text line is prefixed
// with a backslash where it would otherwise be read as grammar (escapeTextLine), and the inline grammar
// of an answer, a hub card, a heading or a machine-spelled arm is escaped where it appears
// (escapeInline). The parser masks escaped pairs (maskEscapes) so the frozen regexes keep their
// meaning on the unescaped grammar only. Contract item F-02; the corpus is samples/synthetic/literal-corpus.json.

/** Where a text line sits inside its step: the first written line, or a later one. */
export type EscapePosition = 'first' | 'later';

/**
 * The inline grammar a string is about to be written into. `label` (added to the spec's five) is the
 * text after `> end:`, `> video:` and `> achieve:`, where only a backslash has a meaning.
 */
export type InlineContext = 'answer' | 'option' | 'heading' | 'hubHeader' | 'arm' | 'label';

/**
 * The NAME pattern (spec B.3): an uppercase letter of any script, then uppercase letters, combining
 * marks, digits, space, `_`, `.`, `-`; an optional `(mood)`. The frozen ASCII set widened; apostrophes
 * are deferred (spec L). Group 1 is the name, group 2 the mood.
 */
export const NAME_PATTERN = /^(\p{Lu}[\p{Lu}\p{M}\p{N} _.-]*?)(?:\s+\(([^)]+)\))?$/u;

/** The trailing tag of a texting, thinking or reaction line (spec C.2): the last token of the line, any case. */
export const CHARACTER_TAG = /\s+\[(texting|thinking|reaction)\]$/i;

// U+0002, twice: the placeholder for an escaped pair. Two characters so a masked string keeps the
// length of the original and every match index maps straight back. Spelled by code point because
// the character is invisible.
const MASK = String.fromCharCode(2, 2);

/** True when the line's first non-space character is a backslash: the line is text, whatever follows. */
export function isEscapedLine(line: string): boolean {
  return /^\s*\\/.test(line);
}

/**
 * True when the line is a written EMPTY text line: its only non-space character is one backslash at
 * the end of the line (spec B.1, "a line that is exactly `\` is an empty text line that continues the
 * paragraph"). `\   ` is NOT one — that is the escape of a line of spaces — and `\\` is the escape of
 * a line holding one backslash.
 */
export function isEmptyTextLine(line: string): boolean {
  return /^\s*\\$/.test(line);
}

/** The line with every escape removed: `\X` yields `X`; a backslash at the very end stays a backslash. */
export function unescapeLine(line: string): string {
  let out = '';
  for (let at = 0; at < line.length; at += 1) {
    const char = line[at];
    if (char === '\\' && at + 1 < line.length) {
      out += line[at + 1];
      at += 1;
    } else {
      out += char;
    }
  }
  return out;
}

/** The line with every escaped pair replaced by two placeholder characters, so grammar searches skip them. */
export function maskEscapes(text: string): string {
  return text.replace(/\\[\s\S]/g, MASK);
}

/** The tag word a character line carries (spec C.2), lowercased; null when the line has no tag. */
export type CharacterLineMode = 'texting' | 'thinking' | 'reaction';

/** What a NAME line says: the name, the optional mood in brackets, and the optional trailing tag. */
export interface CharacterLineShape {
  name: string;
  mood: string | null;
  mode: CharacterLineMode | null;
}

/**
 * The parser's own reading of a NAME line (parse.ts `characterLine`), mirrored here so the
 * decompiler escapes exactly what the parser would take. `characterLine` is internal to the parser,
 * so this function repeats its three steps against the same two exported patterns: an escaped line
 * is never a name; the trailing tag is cut off the end before the NAME pattern is tried; the tag
 * word is lowercased. round1fix-literal.test.ts asserts the two agree over the corpus by parsing.
 */
export function characterLineShape(line: string): CharacterLineShape | null {
  if (isEscapedLine(line)) return null;
  const tag = line.match(CHARACTER_TAG);
  const head = tag && tag.index !== undefined ? line.slice(0, tag.index) : line;
  const match = head.match(NAME_PATTERN);
  if (!match) return null;
  return {
    name: match[1],
    mood: match[2] === undefined ? null : match[2],
    mode: tag ? (tag[1].toLocaleLowerCase() as CharacterLineMode) : null,
  };
}

/** True when the line, its character tag removed, matches the NAME pattern. */
export function isNameShaped(line: string): boolean {
  return characterLineShape(line) !== null;
}

/**
 * True when the parser would read this written line as a step all by itself, with no line under it:
 * a reaction line (spec C, type 10) is the one NAME line that stands alone. A text line of this
 * shape has to be escaped wherever it is written, not only where a line follows it (BAR-R1-02).
 */
export function standsAloneAsStep(line: string): boolean {
  const shape = characterLineShape(line);
  return shape !== null && shape.mode === 'reaction';
}

/**
 * A text line as the decompiler writes it (spec B.1, the table). Every backslash is doubled; then a
 * backslash is put in front of the first non-space character when the line would read as grammar:
 * a leading `*`, `>`, `?` or backslash; a leading `==`; an empty line, wherever it sits (written as a
 * lone backslash, which the parser reads as an empty line that continues the paragraph); a
 * reaction-tagged NAME line, wherever it sits; a NAME-shaped first line of a multi-line text; a
 * NAME-shaped later line that is immediately followed by a non-empty line. `following` is the next
 * line of the same text, null on the last line. A leading hyphen is not escaped (`- ` is grammar
 * only inside a hub option run).
 */
export function escapeTextLine(line: string, position: EscapePosition, following: string | null = null): string {
  const doubled = line.replace(/\\/g, '\\\\');
  // An empty line is written `\`; a line of nothing but spaces is written `\` and the spaces (`\   `),
  // which reads back as the spaces and not as an empty line. Round 1 fix (BAR-R1-02): the LAST line of
  // a text is written the same way. Writing it as it is left the blank line the decompiler puts after
  // the step to end the paragraph, so a text ending in `\n` read back one line shorter than it was.
  if (doubled.trim() === '') return `\\${doubled}`;
  // Round 1 fix (BAR-R1-02): a reaction line is the one NAME line the parser takes with nothing under
  // it (parse.ts, type 10), so it is escaped even as the last line of a text.
  const nameShaped = standsAloneAsStep(line)
    || (isNameShaped(line) && following !== null && (position === 'first' || following.trim() !== ''));
  const lead = doubled.match(/^(\s*)(\S)/) as RegExpMatchArray;
  const first = lead[2];
  // A leading backslash is already escaped by the doubling above, so it is not in this set.
  const grammar = first === '*' || first === '>' || first === '?' || doubled.startsWith('==');
  if (!grammar && !nameShaped) return doubled;
  return `${lead[1]}\\${doubled.slice(lead[1].length)}`;
}

/**
 * Inline text as the decompiler writes it into a grammar position (spec B.1). A backslash is doubled
 * in every context; an answer, a hub card or an arm escapes every ` → ` / ` -> `; an answer or a hub
 * card escapes a trailing ` [<number>]`; a heading escapes every ` @ ` (the parser splits on the last
 * unescaped one); a hub header escapes every `|`.
 */
export function escapeInline(text: string, context: InlineContext): string {
  let out = text.replace(/\\/g, '\\\\');
  if (context === 'answer' || context === 'option' || context === 'arm') {
    out = out.replace(/(\s)(→|->)(?=\s)/g, '$1\\$2');
  }
  if (context === 'answer' || context === 'option') {
    out = out.replace(/(\s)\[(-?\d+(?:\.\d+)?)\]$/, '$1\\[$2]');
  }
  if (context === 'heading') out = out.replace(/ @ /g, ' \\@ ');
  if (context === 'hubHeader') out = out.replace(/\|/g, '\\|');
  return out;
}
